import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { z } from "zod";
import { cached } from "@/lib/server-ttl-cache";
import { isLikelyBot } from "@/lib/bot-ua";
import { onlyLivePosts } from "@/lib/post-visibility";
import { toLatinDigits } from "@/lib/arabic-format";

function serverPublic() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

export const listServices = createServerFn({ method: "GET" }).handler(async () => {
  const sb = serverPublic();
  const { data, error } = await sb
    .from("services")
    .select("id, slug, title_ar, description_ar, duration_min, price_usd, sort_order")
    .eq("is_active", true)
    .order("sort_order", { ascending: true });
  if (error) throw error;
  return data ?? [];
});

export const getServiceBySlug = createServerFn({ method: "GET" })
  .validator((d: { slug: string }) => z.object({ slug: z.string() }).parse(d))
  .handler(async ({ data }) => {
    const sb = serverPublic();
    const { data: row, error } = await sb
      .from("services")
      .select("id, slug, title_ar, description_ar, duration_min, price_usd")
      .eq("slug", data.slug)
      .eq("is_active", true)
      .maybeSingle();
    if (error) throw error;
    return row;
  });

export const listPublishedPosts = createServerFn({ method: "GET" })
  .validator((d: { limit?: number; offset?: number } | undefined) =>
    z.object({ limit: z.number().min(1).max(50).default(12), offset: z.number().min(0).default(0) }).parse(d ?? {}),
  )
  .handler(async ({ data }) => {
    const sb = serverPublic();
    const { data: rows, error, count } = await onlyLivePosts(sb
      .from("posts")
      .select("id, slug, title_ar, excerpt_ar, tldr_ar, cover_image, og_image, image_alt, tags, published_at, updated_at, reading_min, views_count, age_band, rakiza, pillar_slug, related_post_ids", { count: "exact" })
      .order("published_at", { ascending: false }))
      .range(data.offset, data.offset + data.limit - 1);
    if (error) throw error;
    // House rule: Latin numerals everywhere, including inside stored Arabic copy.
    return { rows: toLatinDigits(rows ?? []), total: count ?? 0 };
  });

/**
 * The view counter used to fire one DB write per request, bots included.
 * We keep the counter but only for plausible humans (see `isLikelyBot`), and a
 * failed increment can never affect the response.
 */
export const getPostBySlug = createServerFn({ method: "GET" })
  .validator((d: { slug: string }) => z.object({ slug: z.string() }).parse(d))
  .handler(async ({ data }) => {
    const sb = serverPublic();
    const { data: post, error } = await onlyLivePosts(sb
      .from("posts")
      .select(
        "id, slug, title_ar, excerpt_ar, body_md, cover_image, tags, published_at, reading_min, views_count, meta_description, seo_title, og_image, image_alt, author, author_title, tldr_ar, age_band, rakiza, primary_keyword, faq, mark_phrases, related_post_ids, pillar_slug, cta_kind, boundary_note, updated_at, created_at",
      )
      .eq("slug", data.slug))
      .maybeSingle();
    if (error) throw error;
    if (!post) return null;


    try {
      const { getRequest } = await import("@tanstack/react-start/server");
      const ua = getRequest()?.headers.get("user-agent") ?? null;
      if (!isLikelyBot(ua)) {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        await supabaseAdmin
          .from("posts")
          .update({ views_count: (post.views_count ?? 0) + 1 })
          .eq("id", post.id);
      }
    } catch {
      // A counter must never break a page render.
    }
    return toLatinDigits(post);
  });

/** Resolve curated `related_post_ids` in the exact editorial order given. */
export const listPostsByIds = createServerFn({ method: "GET" })
  .validator((d: { ids: string[] }) => z.object({ ids: z.array(z.string().uuid()).max(12) }).parse(d))
  .handler(async ({ data }) => {
    if (data.ids.length === 0) return [];
    const sb = serverPublic();
    const { data: rows, error } = await onlyLivePosts(sb
      .from("posts")
      .select("id, slug, title_ar, excerpt_ar, tldr_ar, cover_image, og_image, image_alt, reading_min, age_band")
      .in("id", data.ids));
    if (error) throw error;
    const byId = new Map((rows ?? []).map((r) => [r.id, r]));
    return toLatinDigits(data.ids.map((id) => byId.get(id)).filter(Boolean) as NonNullable<typeof rows>);
  });

/** Title of the pillar article, so the "read the pillar" link uses the real headline. */
export const getPostTitleBySlug = createServerFn({ method: "GET" })
  .validator((d: { slug: string }) => z.object({ slug: z.string() }).parse(d))
  .handler(async ({ data }) => {
    const sb = serverPublic();
    const { data: row, error } = await onlyLivePosts(sb
      .from("posts")
      .select("slug, title_ar")
      .eq("slug", data.slug))
      .maybeSingle();
    if (error) throw error;
    return toLatinDigits(row);
  });

/** Slugs of every live post, so article bodies can unlink not-yet-live targets. */
export const listLivePostSlugs = createServerFn({ method: "GET" }).handler(async () => {
  const sb = serverPublic();
  const { data, error } = await onlyLivePosts(sb.from("posts").select("slug"));
  if (error) throw error;
  return (data ?? []).map((r) => r.slug);
});

export const listRelatedPosts = createServerFn({ method: "GET" })
  .validator((d: { excludeSlug: string }) => z.object({ excludeSlug: z.string() }).parse(d))
  .handler(async ({ data }) => {
    const sb = serverPublic();
    const { data: rows, error } = await onlyLivePosts(sb
      .from("posts")
      .select("id, slug, title_ar, excerpt_ar, tldr_ar, cover_image, og_image, image_alt, reading_min, age_band"))
      .neq("slug", data.excludeSlug)
      .order("published_at", { ascending: false })
      .limit(3);
    if (error) throw error;
    return toLatinDigits(rows ?? []);
  });

export const listPaymentInstructions = createServerFn({ method: "GET" }).handler(async () => {
  const sb = serverPublic();
  const { data, error } = await sb
    .from("payment_instructions")
    .select("id, region_slug, title_ar, body_md, sort_order")
    .eq("is_active", true)
    .order("sort_order", { ascending: true });
  if (error) throw error;
  return data ?? [];
});

export const getIntakeForm = createServerFn({ method: "GET" }).handler(async () => {
  const sb = serverPublic();
  const { data, error } = await sb
    .from("forms")
    .select("id, slug, title_ar, description_ar, schema")
    .eq("slug", "consultation-intake")
    .eq("is_active", true)
    .maybeSingle();
  if (error) throw error;
  return data;
});

// Whitelisted public site settings. `site_settings` intentionally has no
// anon SELECT policy — this fn uses the service role but only ever returns
// the four keys below, all of which are safe to expose publicly (social URLs,
// hero image URL, lead magnet URL). Adding a key here is a public exposure
// decision; do not remove the whitelist.
const PUBLIC_SETTING_KEYS = [
  "instagram_url",
  "whatsapp_community_url",
  "hero_image_url",
  "clinic_whatsapp",
  "clinic_address_ar",
] as const;

export type PublicSiteSettings = Record<(typeof PUBLIC_SETTING_KEYS)[number], string>;

export const getPublicSiteSettings = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublicSiteSettings> => {
    // Same for every visitor — memoized so a traffic spike costs one query
    // per worker per minute instead of one per pageview.
    return cached("public:site_settings", 60_000, async () => {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data, error } = await supabaseAdmin
        .from("site_settings")
        .select("key, value")
        .in("key", PUBLIC_SETTING_KEYS as unknown as string[]);
      if (error) throw error;
      const out = Object.fromEntries(
        PUBLIC_SETTING_KEYS.map((k) => [k, ""]),
      ) as PublicSiteSettings;
      for (const row of data ?? []) {
        if ((PUBLIC_SETTING_KEYS as readonly string[]).includes(row.key)) {
          out[row.key as (typeof PUBLIC_SETTING_KEYS)[number]] = row.value ?? "";
        }
      }
      return out;
    });
  },
);
/** Live posts by slug for the home page (read-only, public columns only). */
export const listPostsBySlugs = createServerFn({ method: "GET" })
  .validator((d: { slugs: string[] }) => z.object({ slugs: z.array(z.string().max(120)).max(12) }).parse(d))
  .handler(async ({ data }) => {
    if (data.slugs.length === 0) return [];
    const sb = serverPublic();
    const { data: rows, error } = await onlyLivePosts(sb
      .from("posts")
      .select("slug, title_ar, reading_min, age_band, rakiza, og_image, image_alt")
      .in("slug", data.slugs));
    if (error) throw error;
    return toLatinDigits(rows ?? []);
  });

/** Count of live posts and their reading-time range, for the home blog tile. */
export const getLivePostStats = createServerFn({ method: "GET" }).handler(async () => {
  const sb = serverPublic();
  const { data, error } = await onlyLivePosts(sb.from("posts").select("reading_min"));
  if (error) throw error;
  const mins = (data ?? []).map((r) => Number(r.reading_min)).filter((n) => Number.isFinite(n) && n > 0);
  return {
    count: (data ?? []).length,
    minReading: mins.length ? Math.min(...mins) : null,
    maxReading: mins.length ? Math.max(...mins) : null,
  };
});
